// Excel-style cut / copy / paste for the area plan grid.
//
// The one rule that shapes everything here: CREATE FIRST, DELETE ONLY AFTER THE
// CREATE SUCCEEDS. Ctrl+X never writes to the database -- it only parks the
// selected blocks in memory. The source rows stay live (drawn dashed in the
// grid) until a paste actually lands, so a refused paste, a failed request or a
// planner who changes their mind can never lose a plan.
//
// Timings are corrected by ONE shift for the whole group:
//
//     delta     = target time - earliest start in the held group
//     new start = old start + delta
//     new end   = old end   + delta
//
// Adding the same delta to both ends of every block is what keeps durations and
// the gaps between blocks intact, so a "batch / clean / batch" train pastes back
// as that same train instead of three bars piled on one another. Nothing is
// measured or recomputed.
import { useState, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';

// area_planner stores the activity kind across two columns. These are the same
// spellings the existing payloads in AreaPlanCreate use -- 'maintainance' is
// misspelled in the wire contract and must stay that way.
const toPlanType = (activityType) => {
  if (activityType === 'manufacturing') return 'Operation';
  if (activityType === 'cleaning') return 'Cleaning';
  return 'maintainance';
};

const toPlanActivity = (item) => {
  if (item.activity_type === 'manufacturing') return 'PRODUCTION';
  if (item.activity_type === 'cleaning') return item.cleaning_type || '';
  return item.maintenance_type || '';
};

// Wall-clock formatting only. Every plan time is IST; routing one through
// toISOString() would shift every pasted row by the viewer UTC offset and
// nothing on screen would show it.
const toWireTime = (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : null);

// A held block keeps its ORIGINAL start/end. Offsets are never stored -- adding
// one delta to the originals reproduces them exactly, with less bookkeeping.
export const toClipboardItems = (activityList) =>
  activityList.map((activity) => ({
    sourceId: activity.id,
    activity_type: activity.activity_type,
    batch_number: activity.batch_number || '',
    product_name: activity.product_name || '',
    option_selected: activity.option_selected || '',
    cleaning_type: activity.cleaning_type || null,
    maintenance_type: activity.maintenance_type || null,
    remarks: activity.remarks || '',
    start: activity.start_time,
    // Null end means open-ended. null + delta is meaningless, so it stays null.
    end: activity.end_time || null,
  }));

export const earliestStart = (items) =>
  items.reduce((lowest, item) => {
    const start = dayjs(item.start).valueOf();
    return lowest === null || start < lowest ? start : lowest;
  }, null);

// Pure: held items + a target time -> the same items re-timed. No writes.
export const rebase = (items, targetTime) => {
  const anchor = earliestStart(items);
  if (anchor === null) return [];
  const delta = dayjs(targetTime).valueOf() - anchor;

  return items.map((item) => ({
    ...item,
    newStart: dayjs(item.start).add(delta, 'millisecond'),
    newEnd: item.end ? dayjs(item.end).add(delta, 'millisecond') : null,
  }));
};

export const toPayload = (block, targetAreaId) => {
  const isOperation = block.activity_type === 'manufacturing';
  return {
    eqp_plan_type: toPlanType(block.activity_type),
    eqp_activity: toPlanActivity(block),
    eqp_id: targetAreaId,
    start_date: toWireTime(block.newStart),
    end_date: toWireTime(block.newEnd),
    // dispensing_request_id carries the batch NUMBER, not an id -- existing wire
    // contract, mapped to the product_batch column server-side.
    dispensing_request_id: isOperation ? block.batch_number || '' : '',
    product_name: block.product_name || '',
    option_selected: isOperation ? block.option_selected || '' : '',
    remarks: block.remarks || '',
  };
};

/**
 * Run `task(item, index)` for every item, at most `limit` at a time.
 *
 * Every write path in the area planner used to be a sequential `await` in a
 * loop, so shifting or deleting 20 rows cost 20 round trips one after another.
 * This runs them concurrently while keeping the two things those loops relied
 * on: each row is attempted exactly once, and a failure is reported against the
 * row that actually failed.
 *
 * Returns `{ ok, failed }` in INPUT order, not completion order, so callers get
 * a stable result. Never throws -- a rejecting task becomes an entry in
 * `failed`, so the caller decides what a partial failure means.
 *
 * `limit` defaults to 10 because the Sequelize pool is max 5: beyond that,
 * requests only queue on the server and gain nothing.
 */
export const runBatched = async (items, task, limit = 10) => {
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) return { ok: [], failed: [] };

  // One slot per outcome, filled by whichever worker takes that index. Keeping
  // results positional is what makes the output input-ordered and makes
  // "attributed to the right row" true by construction.
  const outcomes = new Array(list.length);
  const workers = Math.max(1, Math.min(limit, list.length));
  let cursor = 0;

  const worker = async () => {
    for (;;) {
      const index = cursor;
      cursor += 1;                       // single-threaded JS: no torn read
      if (index >= list.length) return;
      try {
        await task(list[index], index);
        outcomes[index] = { ok: true, item: list[index] };
      } catch (error) {
        outcomes[index] = { ok: false, item: list[index], error };
      }
    }
  };

  await Promise.all(Array.from({ length: workers }, worker));

  const ok = [];
  const failed = [];
  outcomes.forEach((outcome) => {
    if (outcome.ok) ok.push(outcome.item);
    else failed.push({ item: outcome.item, error: outcome.error });
  });
  return { ok, failed };
};

/**
 * Soft-delete many rows, preferring one request over N.
 *
 * Fast path: a single call deactivating the whole list. Measured 324s -> ~3s
 * for 100 rows, because it becomes one `WHERE id IN (...)` instead of a
 * hundred round trips.
 *
 * The catch is that one call returns one result, so a partial failure cannot be
 * attributed to a row. Rather than lose that, a failed bulk call falls back to
 * deleting one at a time through runBatched, which restores exact per-row
 * reporting. The fast path is an optimisation; the slow path is the contract.
 *
 * @param ids         row ids to deactivate
 * @param deleteMany  (ids) => Promise    -- the single-call API
 * @param deleteOne   (id)  => Promise    -- the per-row API, used on fallback
 * @returns { ok: [...ids], failed: [{ item: id, error }], usedFallback: bool }
 */
export const deleteManyWithFallback = async (ids, deleteMany, deleteOne, limit = 10) => {
  const list = Array.isArray(ids) ? ids.filter((id) => id !== null && id !== undefined) : [];
  if (list.length === 0) return { ok: [], failed: [], usedFallback: false };

  try {
    await deleteMany(list);
    return { ok: [...list], failed: [], usedFallback: false };
  } catch (bulkError) {
    console.warn('Bulk delete failed, falling back to one at a time:', bulkError?.message || bulkError);
    const result = await runBatched(list, (id) => deleteOne(id), limit);
    return { ...result, usedFallback: true };
  }
};

/**
 * Normalize the "ignore these rows" argument of an overlap check.
 *
 * Accepts a single id (what the original caller passes), an array, a Set, or
 * nothing. Lives here rather than in the component so the cut-into-the-same-area
 * path -- which depends entirely on the array form working -- is covered.
 */
export const toExcludedSet = (excludeActivityId) => {
  if (excludeActivityId === null || excludeActivityId === undefined) return new Set();
  if (excludeActivityId instanceof Set) return new Set(excludeActivityId);
  if (Array.isArray(excludeActivityId)) return new Set(excludeActivityId);
  return new Set([excludeActivityId]);
};

/**
 * Which held blocks land on hours the target area has already booked.
 *
 * Each block is compared against what is ALREADY in the target area, never
 * against its siblings: overlaps inside a selection are real plan (packing rooms
 * run two machines on one batch over the same hours), so they must survive.
 *
 * excludeIds carries the source rows of a cut -- they are about to be removed,
 * so they must not block a paste back into the same area. A copy passes none,
 * because its source rows are staying.
 */
export const findCollisions = (blocks, targetAreaId, checkForOverlaps, excludeIds = []) => {
  const collisions = [];
  blocks.forEach((block) => {
    const result = checkForOverlaps(
      targetAreaId,
      block.newStart,
      block.newEnd || block.newStart,
      excludeIds
    );
    if (result.hasOverlap) {
      collisions.push({ block, blockedBy: result.overlappingActivities });
    }
  });
  return collisions;
};

// The grid renders a fixed 7 days, so a multi-day selection can land blocks the
// planner will not see until they navigate.
export const findOutOfWeek = (blocks, weekEnd) => {
  if (!weekEnd) return [];
  const cutoff = dayjs(weekEnd).valueOf();
  return blocks.filter((block) => block.newStart.valueOf() > cutoff);
};

/**
 * Create the new rows, and only then remove the old ones.
 *
 * Extracted from the hook so this ordering -- the part that can corrupt a plan
 * -- is directly testable with an injected createPlan.
 *
 * Returns { created, deleteFailures, consumed }. `consumed` tells the caller
 * whether the clipboard is spent (a cut is, a copy is not). `deleteFailures` is
 * non-empty only in the one genuinely bad case: the create landed but a delete
 * did not, leaving the activity in both areas.
 */
export const applyPaste = async ({ op, items, blocks, targetAreaId, createPlan, deleteMany = null }) => {
  // 1. CREATE -- one request for every block. If this throws, nothing has been
  //    removed and the caller still holds the clipboard.
  const payloads = blocks.map((block) => toPayload(block, targetAreaId));
  await createPlan(payloads);

  // 2. A copy is finished here, and keeps the clipboard so the planner can fill
  //    several areas.
  if (op === 'copy') {
    return { created: payloads.length, deleteFailures: [], consumed: false };
  }

  // 3. DELETE the source rows. The backend has no bulk delete, so this is still
  //    one request per row -- but concurrently rather than one after another,
  //    which is the difference between ~N round trips and ~1. Each failure is
  //    retried once, and only the failures are retried.
  const removeSource = (item) => createPlan({ id: item.sourceId, is_active: false });
  const sourceIds = items.map((item) => item.sourceId);
  const byId = new Map(items.map((item) => [item.sourceId, item]));

  // One request for the whole cut when a bulk deleter is available; otherwise,
  // and on any bulk failure, one at a time so failures stay per-row.
  const firstPass = deleteMany
    ? await deleteManyWithFallback(sourceIds, deleteMany, (id) => removeSource(byId.get(id)))
    : await runBatched(items, removeSource);

  const stillFailing = firstPass.failed.map((entry) => entry.item);
  const retryPass = stillFailing.length
    ? await runBatched(stillFailing, (entry) => removeSource(byId.get(entry) || entry))
    : { ok: [], failed: [] };

  retryPass.failed.forEach(({ item, error }) => {
    const sourceId = item?.sourceId ?? item;
    console.error(`Could not remove source activity ${sourceId}:`, error);
  });
  const deleteFailures = retryPass.failed.map(({ item }) => byId.get(item) || item);

  // A cut is consumed by its paste whether or not every delete landed -- rows
  // left behind are reported, not silently re-pasteable.
  return { created: payloads.length, deleteFailures, consumed: true };
};

/**
 * @param createPlan  createAreaPlan from api.js -- takes an array to bulk
 *                    create, or { id, is_active: false } to soft delete.
 * @param checkForOverlaps  the overlap check already in AreaPlanCreate, so the
 *                    collision rules stay in one place. Signature:
 *                    (areaId, start, end, excludeIds) ->
 *                    { hasOverlap, overlappingActivities }
 */
export const useAreaPlanClipboard = ({ createPlan, deleteMany = null, checkForOverlaps }) => {
  // { op: 'cut' | 'copy', sourceAreaId, sourceAreaName, items: [...] }
  const [clipboard, setClipboard] = useState(null);
  const [pasting, setPasting] = useState(false);

  const isHolding = !!clipboard;

  // Source rows of a pending cut, so the grid can draw them dashed.
  const heldIds = useMemo(() => {
    if (!clipboard || clipboard.op !== 'cut') return new Set();
    return new Set(clipboard.items.map((item) => item.sourceId));
  }, [clipboard]);

  const hold = useCallback((op, activityList, areaId, areaName) => {
    if (!activityList.length) return false;
    setClipboard({
      op,
      sourceAreaId: areaId,
      sourceAreaName: areaName || areaId,
      items: toClipboardItems(activityList),
    });
    return true;
  }, []);

  const clear = useCallback(() => setClipboard(null), []);

  /**
   * Re-time the held blocks for a target and report what stands in the way.
   * Pure with respect to the database -- nothing is written here.
   */
  const planPaste = useCallback(
    (targetAreaId, targetTime, weekEnd) => {
      if (!clipboard) return null;

      const blocks = rebase(clipboard.items, targetTime);
      const excludeIds =
        clipboard.op === 'cut' ? clipboard.items.map((item) => item.sourceId) : [];

      return {
        op: clipboard.op,
        blocks,
        collisions: findCollisions(blocks, targetAreaId, checkForOverlaps, excludeIds),
        outOfWeek: findOutOfWeek(blocks, weekEnd),
        targetAreaId,
      };
    },
    [clipboard, checkForOverlaps]
  );

  /**
   * Create the new rows, and only then remove the old ones.
   *
   * Returns { created, deleteFailures }. deleteFailures is non-empty only in the
   * one genuinely bad case -- the create landed but a delete did not -- which
   * leaves the activity in both rooms. The caller must surface that plainly
   * rather than leave a silent duplicate.
   */
  const commitPaste = useCallback(
    async (planned) => {
      if (!clipboard || !planned) return { created: 0, deleteFailures: [] };
      if (planned.collisions.length) {
        throw new Error('Cannot paste onto occupied hours');
      }

      setPasting(true);
      try {
        const { created, deleteFailures, consumed } = await applyPaste({
          op: clipboard.op,
          items: clipboard.items,
          blocks: planned.blocks,
          targetAreaId: planned.targetAreaId,
          createPlan,
          deleteMany,
        });

        if (consumed) setClipboard(null);
        return { created, deleteFailures };
      } finally {
        setPasting(false);
      }
    },
    [clipboard, createPlan, deleteMany]
  );

  return { clipboard, isHolding, heldIds, pasting, hold, clear, planPaste, commitPaste };
};

export default useAreaPlanClipboard;
